import { cloneElement, createContext, isValidElement, useContext, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { mdiChevronLeft } from '@mdi/js';
import Icon from '@mdi/react';
import { useIntl } from 'react-intl';
import styles from './CollapsibleSnackBar.module.scss';

const CollapsibleSnackBarContext = createContext();

export const useCollapsibleSnackBar = () => {
    const ctx = useContext(CollapsibleSnackBarContext);
    if (!ctx) {
        throw new Error('CollapsibleSnackBar.* must be used within <CollapsibleSnackBar>.');
    }
    return ctx;
};

const CollapsibleSnackBar = ({
    icon,
    label,
    children,
    iconColor,
    collapsedContent,
    collapseSignal,
    expandSignal,
    onUserCollapse,
    onUserExpand,
    onExpand,
    ...props
}) => {
    const intl = useIntl();
    const [show, setShow] = useState(true);
    const contextValue = useMemo(() => ({ show, setShow }), [show]);

    const didMountRef = useRef(false);
    useEffect(() => {
        if (!didMountRef.current) {
            didMountRef.current = true;
            return;
        }
        if (collapseSignal != null) {
            setShow(false);
        }
    }, [collapseSignal]);

    const clickHandler = () => {
        if (show && onUserCollapse) {
            onUserCollapse();
        }
        if (!show && onUserExpand) {
            onUserExpand();
        }

        setShow(prev => !prev);
    };
    useEffect(() => {
        if (expandSignal != null) {
            if (onExpand) {
                onExpand();
            }
            setShow(true);
        }
    }, [expandSignal]);

    return <CollapsibleSnackBarContext.Provider value={contextValue}>
        <div className={styles.container} {...props}>
            <div className={styles.icon}>
                <Icon path={icon} size={1} />
            </div>
            {!show && collapsedContent}
            {show ? <div className={styles.content}>{children}</div> : null}
            <button
                className={styles.close}
                data-testid="snackbar.collapse"
                aria-label={`${label} ${intl.formatMessage({ id: 'general.notification.details' })}`}
                aria-expanded={show}
                onClick={clickHandler}
            >
                <Icon path={mdiChevronLeft} size={1} rotate={show ? 0 : 180} color={iconColor} />
            </button>
        </div>
    </CollapsibleSnackBarContext.Provider>;
};

const CloseTrigger = ({ children }) => {
    const { setShow } = useCollapsibleSnackBar();

    if (!isValidElement(children)) {
        return children;
    }

    const injectedProps = {};

    if (children.props.onPress) {
        injectedProps.onPress = async (e) => {
            await children.props.onPress(e);
            setShow(false);
        };
    } else {
        injectedProps.onClick = async (e) => {
            await children.props.onClick?.(e);
            setShow(false);
        };
    }
    return cloneElement(children, injectedProps);
};

CollapsibleSnackBar.CloseTrigger = CloseTrigger;

CollapsibleSnackBar.propTypes = {
    icon: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    collapsedContent: PropTypes.node,
    collapseSignal: PropTypes.any,
    onUserCollapse: PropTypes.func,
    expandSignal: PropTypes.any,
    onExpand: PropTypes.func,
    onUserExpand: PropTypes.func,
    iconColor: PropTypes.oneOf(['black', 'white']),
    children: PropTypes.any
};

CollapsibleSnackBar.defaultProps = {
    iconColor: 'white',
    collapsedContent: null,
    collapseSignal: null,
    expandSignal: null
};

export default CollapsibleSnackBar;
